import { Component, computed, inject, input, model, signal } from '@angular/core';
import { ColDef, GridOptions } from 'ag-grid-community';
import { finalize } from 'rxjs';
import { defaultTableDefOptions } from '../../../../helpers';
import { BranchService } from '../../../../service/branch.service';
import { BranchDetailsResponse, KioskProvider } from '../../../../types';
import { CellInstallerDashboardKioskOrganizationActionsComponent } from '../cell-installer-dashboard-kiosk-organization-actions/cell-installer-dashboard-kiosk-organization-actions.component';

@Component({
  selector: 'app-installer-dashboard-table-kiosks',
  standalone: false,
  templateUrl: './installer-dashboard-table-kiosks.component.html',
  styleUrl: './installer-dashboard-table-kiosks.component.scss',
})
export class InstallerDashboardTableKiosksComponent {
  private branchService = inject(BranchService);

  public branchId = input.required<number>();

  public isHandlingProviders = signal<boolean>(false);
  public allKioskProviders = input.required<KioskProvider[]>();
  public allBranchAliases = model.required<BranchDetailsResponse['kiosk_providers']>();

  public selectedKioskProvider = signal<KioskProvider | undefined>(undefined);
  public selectedDataSource = signal<string | undefined>(undefined);

  public filteredKioskProviders = computed(() => {
    const dataSource = this.selectedDataSource();
    return this.allKioskProviders().filter((k) => k.data_source?.toLowerCase() === dataSource?.toLowerCase());
  });

  public dataSources = computed(() => Array.from(new Set(this.allKioskProviders().map((provider) => provider.data_source.toLowerCase()))));

  public readonly kioskOrganizationColumns: ColDef[] = [
    {
      field: 'alias',
      headerName: 'Organization Name',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 500,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'data_source',
      headerName: 'Data Source',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'external_id',
      headerName: 'External ID',
      cellDataType: 'text',
      minWidth: 200,
      maxWidth: 400,
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 70,
      maxWidth: 70,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellInstallerDashboardKioskOrganizationActionsComponent,
      cellRendererParams: {
        deleteCallback: (item: KioskProvider) => {
          this.removeKioskProvider(item);
        },
      },
    },
  ];

  public readonly kioskOrganizationTable: GridOptions<KioskProvider> = {
    ...defaultTableDefOptions(),
  };

  onSelectProvider(event: any) {
    event = {
      ...event,
      data_source: this.selectedDataSource()!,
    };

    this.selectedKioskProvider.set(event);
  }

  addKioskProvider() {
    this.isHandlingProviders.set(true);
    this.branchService
      .addKioskProvider(this.branchId(), this.selectedKioskProvider())
      .pipe(finalize(() => this.isHandlingProviders.set(false)))
      .subscribe((createdProvider) => {
        this.selectedKioskProvider.set(undefined);
        this.allBranchAliases.set([...this.allBranchAliases(), createdProvider]);
      });
  }

  removeKioskProvider(item: KioskProvider) {
    this.isHandlingProviders.set(true);
    this.branchService
      .removeKioskProvider(this.branchId(), item.id)
      .pipe(finalize(() => this.isHandlingProviders.set(false)))
      .subscribe(() => {
        this.allBranchAliases.set(this.allBranchAliases().filter((f) => f.id !== item.id));
      });
  }
}
